import { Upload } from "antd";
import {
  UploadOutlined,
  SendOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import "./SessionTable.scss";
import { freeOrPaidOptions } from "../../../data/nivoConsts";
import useSessionTable, { SessionTableProps } from "./useSessionTable";
import Button from "../../../atoms/Button/Button";
import { useState } from "react";
import Table, { Column } from "../../../atoms/Table/Table";
import Textarea from "../../../atoms/TextArea/Textarea";
import Input from "../../../atoms/Input/Input";
import Dropdown from "../../../atoms/Dropdown";
import Checkbox from "../../../atoms/Checkbox/Checkbox";
import DischargeSummary from "../DischargeSummary";
import Loader from "../../../atoms/Loader";
import ConfirmationModal from "../../../molecules/CgConfirmationModal/ConfirmationModal";

// const { Option } = Select;

const SessionTable = (props: SessionTableProps) => {
  const { formData, formObject, isNivo = false, updateFormObject } = props;
  const todayStr = new Date().toISOString().split("T")[0];
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [showTherapyError, setShowTherapyError] = useState(false);
  const [showIndicationError, setShowIndicationError] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const {
    handleAddRow,
    handleRemoveRow,
    handleAddDocument,
    isSessionFormValid,
    handleSessionChange, // for previous session table
    isDocumentUploadValid,
    handleUpcomingChange, // for upcoming session row
    isUpcomingSessionValid,
    handleBulkSubmission,
    submissionLoading,
    submissionStatus,
  } = useSessionTable(props);

  function truncateFileName(file: any, type: string = "ds") {
    const maxLength = 100;
    if (!file || !file.name)
      return type === "ds"
        ? "Upload Discharge Summary"
        : "Upload Purchase Invoice";
    const fileName = file.name;
    if (fileName.length <= maxLength) return fileName;
    const extIndex = fileName.lastIndexOf(".");
    if (extIndex === -1 || extIndex === 0) return fileName.slice(0, maxLength);
    const ext = fileName.slice(extIndex);
    const base = fileName.slice(0, maxLength - ext.length - 3); // 3 for "..."
    return `${base}...${ext}`;
  }

  const prevSessionColumns = [
    {
      label: "Session/Cycle Number",
      accessor: "visit_cycle",
      align: "center",
      width: 120,
      // key: "visit_cycle",
      // width: 120,
      // align: "center" as const,
      render: (_: any, row: any, rowIndex: number) => rowIndex + 1,
    },
    {
      label: "Session/Cycle Date*",
      accessor: "follow_up_date",
      // key: "follow_up_date",
      // align: "center" as const,
      render: (_: any, row: any, rowIndex: number) => {
        // Calculate max date (yesterday)
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        let maxDate = yesterday.toISOString().split("T")[0];
        // Calculate min date (previous row's follow_up_date)
        let minDate;
        if (rowIndex > 0) {
          const prevRow = formObject.patientVisits[rowIndex - 1];
          if (prevRow?.follow_up_date) {
            const prevDate = new Date(prevRow.follow_up_date);
            prevDate.setDate(prevDate.getDate() + 1);
            minDate = prevDate.toISOString().split("T")[0];
          }
        }
        const isError =
          showValidationErrors && !isSessionFormValid && !row.follow_up_date;
        return (
          <Input
            type="date"
            value={row.follow_up_date}
            placeholder="DD/MM/YYYY"
            min={minDate}
            max={maxDate}
            onChange={(e: any) =>
              handleSessionChange(rowIndex, "follow_up_date", e.target.value)
            }
            error={isError}
            helperText={isError ? "Session/Cycle Date is required" : undefined}
          />
        );
      },
    },
    isNivo && {
      label: "Dosage(mg)",
      accessor: "dosage",
      render: (_: any, row: any, rowIndex: number) => (
        <Input
          name="dosage"
          id="dosage"
          type="number"
          value={row.dosage}
          placeholder="Dosage"
          onChange={(e: any) =>
            handleSessionChange(rowIndex, "dosage", e.target.value)
          }
          error={showValidationErrors && !isSessionFormValid}
          helperText={
            showValidationErrors && !isSessionFormValid ? "Required" : undefined
          }
        />
      ),
    },
    {
      label: "Approve coins",
      accessor: "approved",
      // key: "approved",
      // align: "center" as const,
      render: (_: any, row: any, rowIndex: number) => (
        <Checkbox
          checked={row.approved}
          onChange={(e) =>
            handleSessionChange(rowIndex, "approved", e.target.checked)
          }
        >
          Add 250 Care Coins
        </Checkbox>
      ),
    },
    {
      label: "Invoice*",
      accessor: "invoice",
      // key: "invoice",
      // align: "center" as const,
      align: "center" as const,
      render: (_: any, row: any, rowIndex: number) => {
        const isLastSession =
          row.visit_cycle == formObject.patientVisits.length;
        const isPurchaseInvoiceError =
          showValidationErrors && !isDocumentUploadValid && !row.pi;
        const isDischargeSummaryError =
          isLastSession &&
          showValidationErrors &&
          !isDocumentUploadValid &&
          !row.ds;
        return (
          <div className="flex gap-2 flex-col items-center">
            <Upload
              accept=".pdf,.jpg,.jpeg,.png"
              showUploadList={false}
              beforeUpload={(file) => {
                handleAddDocument(rowIndex, file, row, "pi");
                return false;
              }}
            >
              <Button
                iconStart={<UploadOutlined />}
                onClick={() => {}}
              >
                {truncateFileName(row.pi, "pi")}
                {/* Upload Purchase Invoice */}
              </Button>
            </Upload>
            {isLastSession && (
              <Upload
                accept=".pdf,.jpg,.jpeg,.png"
                showUploadList={false}
                beforeUpload={(file) => {
                  handleAddDocument(rowIndex, file, row, "ds");
                  return false;
                }}
              >
                <Button
                  iconStart={<UploadOutlined />}
                  variant="outlined"
                  onClick={() => {}}
                >
                  {truncateFileName(row.ds, "ds")}
                </Button>
              </Upload>
            )}
            {isPurchaseInvoiceError && (
              <span className="helper-text error-text">
                Purchase Invoice is required
              </span>
            )}
            {isDischargeSummaryError && (
              <span className="helper-text error-text">
                Discharge Summary is required
              </span>
            )}
          </div>
        );
      },
    },
    {
      label: "Actions",
      accessor: "actions",
      // key: "actions",
      // align: "center" as const,
      render: (_: any, row: any, rowIndex: number) => (
        <Button
          variant="outlined"
          color="error"
          onClick={() => handleRemoveRow(rowIndex)}
        >
          <DeleteOutlined size={24} />
        </Button>
      ),
    },
  ].filter(Boolean) as Column[];

  const upcomingColumns: Column[] = [
    {
      label: "Session/Cycle Number",
      accessor: "visit_cycle",
      align: "center",
      width: 120,
      // key: "visit_cycle",
      // align: "center" as const,
      render: (_: any, row: any, rowIndex: number) => (
        <Input
          name="visit_cycle"
          id="visit_cycle"
          value={formObject.patientVisits.length + 1}
          disabled
          type="number"
          placeholder="Enter Session/Cycle Number"
        />
      ),
    },
    {
      label: "Upcoming cycle/session date*",
      accessor: "follow_up_date",
      // key: "follow_up_date",
      // align: "center" as const,
      render: (_: any, row: any, rowIndex: number) => {
        const isError =
          showValidationErrors &&
          !isUpcomingSessionValid &&
          !row.follow_up_date;
        return (
          <Input
            name="follow_up_date"
            id="follow_up_date"
            type="date"
            value={formObject.upcomingSession.follow_up_date}
            placeholder="DD/MM/YYYY"
            min={todayStr}
            onChange={(e: any) =>
              handleUpcomingChange("follow_up_date", e.target.value)
            }
            error={isError}
            helperText={
              isError ? "Upcoming cycle/session date is required" : undefined
            }
          />
        );
      },
    },
    isNivo && {
      label: "Upcoming Dosage(mg)",
      accessor: "dosage",
      // key: "dosage",
      // align: "center" as const,
      render: (_: any, row: any, rowIndex: number) => (
        <Input
          type="number"
          value={formObject.upcomingSession.dosage}
          onChange={(e: any) => handleUpcomingChange("dosage", e.target.value)}
          error={showValidationErrors && !isUpcomingSessionValid}
          helperText={
            showValidationErrors && !isUpcomingSessionValid
              ? "Required"
              : undefined
          }
        />
      ),
    },
    isNivo && {
      label: "Free or paid Drug?",
      accessor: "is_drug_free",
      render: (_: any, row: any, rowIndex: number) => (
        <Dropdown
          options={freeOrPaidOptions}
          value={formObject.upcomingSession.is_drug_free}
          onChange={(e: any) =>
            handleUpcomingChange("is_drug_free", e.target.value)
          }
          placeholder="Free or paid Drug?"
          error={showValidationErrors && !isUpcomingSessionValid}
          helperText={
            showValidationErrors && !isUpcomingSessionValid
              ? "Required"
              : undefined
          }
        />
        // <Select
        //   value={formObject.upcomingSession.is_drug_free}
        //   onChange={(value) => handleUpcomingChange("is_drug_free", value)}
        //   style={{ minWidth: 120 }}
        //   placeholder="Free or paid Drug?"
        // >
        //   {freeOrPaidOptions.map((option) => (
        //     <Option key={option.value} value={option.value}>
        //       {option.label}
        //     </Option>
        //   ))}
        // </Select>
      ),
    },
    {
      label: "Add Notes*",
      accessor: "notes",
      // key: "notes",
      // align: "center" as const,
      render: (_: any, row: any, rowIndex: number) => {
        const isError =
          showValidationErrors && !isUpcomingSessionValid && !row.notes;
        return (
          <Textarea
            name="notes"
            value={formObject.upcomingSession.notes || ""}
            placeholder="Add Notes"
            onChange={(e) => handleUpcomingChange("notes", e.target.value)}
            rows={1}
            error={isError}
            helperText={isError ? "Notes are required" : undefined}
          />
        );
      },
    },
  ].filter(Boolean) as Column[];

  const handleClosePopUp = () => {
    setConfirmOpen(false);
  };

  const handleConfirmation = (takeAction: boolean) => {
    handleClosePopUp();
    if (takeAction) handleBulkSubmission();
  };

  return (
    <>
      {submissionLoading && (
        <div className="loaderAbsolute">
          <Loader noBackground={true} text={submissionStatus} />
        </div>
      )}
      <DischargeSummary
        formData={formData}
        formObject={formObject}
        updateFormObject={updateFormObject}
        showTherapyError={showTherapyError}
        showIndicationError={showIndicationError}
        setShowTherapyError={setShowTherapyError}
        setShowIndicationError={setShowIndicationError}
      />
      <div className="session-table-v2">
        <div className="py-4 flex px-4">
          <div className="text-lg font-bold">Previous session(s)</div>
        </div>
        <Table columns={prevSessionColumns} data={formObject.patientVisits} />
        {showValidationErrors &&
          (!isSessionFormValid || !isDocumentUploadValid) && (
            <p className="text-red-500 text-sm px-4 pb-2 m-0">
              {!isSessionFormValid &&
                "Complete all session dates and make sure to select therapy. "}
              {!isDocumentUploadValid &&
                "Upload Purchase Invoice for each session and Discharge Summary for the last session."}
            </p>
          )}
        <div className="py-4 flex justify-end gap-4 px-4 items-center">
          <Button
            variant="outlined"
            iconStart={<PlusOutlined />}
            onClick={handleAddRow}
          >
            Add new cycle
          </Button>
        </div>
      </div>
      <div className="session-table-v2 mt-6">
        {/* Upcoming session */}
        <div
          style={{
            boxShadow: "0 0 10px 0 rgba(0, 0, 0, 0.1)",
            borderRadius: "16px",
            //marginTop: 32,
          }}
        >
          <div className="py-4 flex px-4">
            <div className="text-lg font-bold">Upcoming session</div>
          </div>
          <Table
            columns={upcomingColumns}
            data={[formObject.upcomingSession]}
          />
          {showValidationErrors && !isUpcomingSessionValid && (
            <p className="text-red-500 text-sm px-4 pb-2 m-0">
              Please fill all required upcoming session fields.
            </p>
          )}
          <div className="py-4 flex justify-end gap-4 px-4 items-center">
            <Button
              iconEnd={<SendOutlined />}
              onClick={() => {
                const valid =
                  isSessionFormValid &&
                  isDocumentUploadValid &&
                  isUpcomingSessionValid;
                if (valid) setConfirmOpen(true);
                else {
                  setShowValidationErrors(true);
                  if (!formObject.therapy) {
                    // Therapy is required
                    setShowTherapyError(true); // Show therapy error
                  }
                  if (!formObject.therapy_indication) {
                    // Indication is required
                    setShowIndicationError(true); // Show indication error
                  }
                }
              }}
              loading={submissionLoading}
            >
              <div>Bulk cycle/session creation</div>
            </Button>
          </div>
        </div>
      </div>
      {confirmOpen && (
        <div
          className={`confirmation-modal ${
            confirmOpen ? "overlay visible" : ""
          }`}
        >
          <ConfirmationModal
            show={confirmOpen}
            onConfirmation={handleConfirmation}
            onClose={handleClosePopUp}
          />
        </div>
      )}
    </>
  );
};

export default SessionTable;
